import { faQrcode } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { AxiosError } from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import React, { useCallback, useState } from 'react';

import PersonRepository from '@/repositories/PersonRepository';
import { Alert } from '@/ui/Alert';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { CopyContent } from '@/ui/CopyContent';
import { Pill } from '@/ui/Pill';
import { Typography } from '@/ui/Typography';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import './styles.css';
import type { Props } from './types';

const ImpersonateCard = ({ personId, name }: Props) => {
  const personRepository = PersonRepository();
  const [loginLink, setLoginLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const generate = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await personRepository.impersonate(personId);
      setLoginLink(response?.data?.link || '');
    } catch (error) {
      const errorMessages = mapAxiosErrorMessages(error as AxiosError);
      setError(errorMessages[0]?.text || 'Failed to generate impersonation token');
    } finally {
      setIsLoading(false);
    }
  }, [personId, personRepository]);

  return (
    <Card padding="lg">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Typography variant="subheading" as="h3">
            Impersonate
          </Typography>
          <Typography muted>
            Generate a one-time login that opens the app as{' '}
            <span className="mp-sensitive">{name}</span>.
          </Typography>
        </div>
        <Pill variant="subtle" color="destructive" size="sm">
          Engineering only
        </Pill>
      </div>

      <div className="mt-5 space-y-4 border-t border-uniform-gray-200 pt-5">
        <Alert variant="warning">
          Every request you make is recorded against your account, not{' '}
          <span className="mp-sensitive">{name}</span>&apos;s.{' '}
          <span className="mp-sensitive">{name}</span> stays logged in on their own devices.
        </Alert>

        {error && <Alert variant="error" message={error} />}

        <div className="flex flex-col items-center gap-4 rounded-lg border border-uniform-gray-200 bg-uniform-gray-50 p-6">
          {loginLink ? (
            <>
              <Typography muted>Scan this QR code with the app to log in</Typography>
              <QRCodeSVG
                className="rounded-lg border border-uniform-gray-200 bg-white p-3"
                value={loginLink}
                size={224}
              />
              <div className="w-full space-y-1">
                <Typography variant="label" muted>
                  One-time login link
                </Typography>
                <CopyContent
                  content={loginLink}
                  className="rounded-md border border-uniform-gray-200 bg-white px-3 py-2">
                  <span className="break-all font-mono text-xs text-uniform-gray-700">
                    {loginLink}
                  </span>
                </CopyContent>
              </div>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faQrcode} className="h-10 w-10 text-uniform-gray-300" />
              <Typography muted>No login generated yet.</Typography>
            </>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={generate} isLoading={isLoading}>
            {loginLink ? 'Generate new QR code' : 'Generate QR code'}
          </Button>
        </div>

        <ul className="list-disc space-y-1 pl-5 text-xs text-uniform-gray-600">
          <li>The link works once, and expires after use or after a set time.</li>
          <li>The session it opens expires on its own.</li>
          <li>Use it only for legitimate support purposes.</li>
        </ul>
      </div>
    </Card>
  );
};

export default React.memo(ImpersonateCard);
